import api from '../services/api';

export const logisticsOpsApi = {
  listCatalog: (params?: { status?: string; q?: string; barcode?: string }) =>
    api.get('/product-catalog', { params }).then((r) => r.data),

  createProduct: (body: {
    barcodes: { type: string; value: string }[];
    description?: string;
    manufacturer?: string;
  }) => api.post('/product-catalog', body).then((r) => r.data),

  updateProduct: (id: string, patch: { description?: string; status?: string }) =>
    api.patch(`/product-catalog/${id}`, patch).then((r) => r.data),

  deleteProduct: (id: string) => api.delete(`/product-catalog/${id}`).then((r) => r.data),

  importCatalog: (products: any[]) =>
    api.post('/product-catalog/import', { products }).then((r) => r.data),

  importCatalogFile: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/product-catalog/import-file', form).then((r) => r.data);
  },

  listCommissioned: (params?: {
    objectType?: string;
    status?: string;
    gtin?: string;
    since?: string;
  }) =>
    api
      .get('/inventory', { params: { source: 'RFID', ...(params || {}) } })
      .then((r) => r.data),

  issueEpcs: (body: {
    barcode: string;
    quantity?: number;
    batchLot?: string;
    expirationDate?: string;
    locationId?: string;
  }) => api.post('/commissioning/issue', body).then((r) => r.data),

  renderLabels: (items: {
    epc: string;
    batchLot?: string;
    expirationDate?: string;
    description?: string;
  }[]) => api.post('/commissioning/labels/zpl', { items }).then((r) => r.data),

  getTraceability: (epc: string) => api.get(`/traceability/${epc}`).then((r) => r.data),

  rebuildTraceability: (epc: string) =>
    api.post(`/traceability/${epc}/rebuild`).then((r) => r.data),

  listVerificationDocs: () => api.get('/verification/documents').then((r) => r.data),

  importVerificationDoc: (body: {
    name: string;
    operationType: string;
    lines: { productCode: string; expectedQty: number }[];
  }) => api.post('/verification/documents', body).then((r) => r.data),

  getVerificationSession: (id: string) =>
    api.get(`/verification/sessions/${id}`).then((r) => r.data),

  getFeatureConfig: () => api.get('/features/config').then((r) => r.data),

  listLocations: (params?: { page?: number; limit?: number; type?: string; search?: string; parentLocationId?: string }) =>
    api.get('/locations', { params }).then((r) => r.data),

  getLocationTree: () => api.get('/locations/tree').then((r) => r.data),

  createLocation: (body: {
    code: string;
    name: string;
    type: string;
    parentLocationId?: string;
    blingDepositoId?: string;
    metadata?: Record<string, unknown>;
  }) => api.post('/locations', body).then((r) => r.data),

  updateLocation: (
    id: string,
    patch: { code?: string; name?: string; type?: string; parentLocationId?: string | null; active?: boolean },
  ) => api.put(`/locations/${id}`, patch).then((r) => r.data),

  deleteLocation: (id: string) => api.delete(`/locations/${id}`).then((r) => r.data),

  tagLocation: (id: string, body: { captureMethod: 'RFID' | 'QR_CODE' | 'BARCODE'; rawValue: string }) =>
    api.post(`/locations/${id}/tag`, body).then((r) => r.data),

  importLocationsFile: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/locations/import', form).then((r) => r.data);
  },

  listReceivingDocuments: (params?: { status?: string }) =>
    api.get('/receiving/documents', { params }).then((r) => r.data),

  getReceivingDocument: (id: string) =>
    api.get(`/receiving/documents/${id}`).then((r) => r.data),

  importNfe: (file: File, destinationLocationId: string, overwrite?: boolean) => {
    const form = new FormData();
    form.append('file', file);
    form.append('destinationLocationId', destinationLocationId);
    if (overwrite) form.append('overwrite', 'true');
    return api.post('/receiving/documents/import-nfe', form).then((r) => r.data);
  },

  createReceivingDocument: (body: {
    destinationLocationId: string;
    items?: { productId?: string; epc?: string; quantity: number }[];
  }) => api.post('/receiving/documents', body).then((r) => r.data),

  updateReceivingDocument: (
    id: string,
    patch: {
      destinationLocationId?: string;
      items?: { productId?: string; epc?: string; quantity: number }[];
    },
  ) => api.patch(`/receiving/documents/${id}`, patch).then((r) => r.data),

  deleteReceivingDocument: (id: string) =>
    api.delete(`/receiving/documents/${id}`).then((r) => r.data),

  getReceivingDocumentByCode: (code: string) =>
    api.get(`/receiving/documents/by-code/${encodeURIComponent(code)}`).then((r) => r.data),

  scanReceivingDocument: (
    id: string,
    payload: { epc: string; barcode?: string; deviceSerial?: string; batchLot?: string; expirationDate?: string },
  ) => api.post(`/receiving/documents/${id}/scan`, payload).then((r) => r.data),

  completeReceivingDocument: (id: string, override = false) =>
    api.post(`/receiving/documents/${id}/complete`, { override }).then((r) => r.data),

  listShippingDocuments: (params?: { status?: string }) =>
    api.get('/shipping/documents', { params }).then((r) => r.data),

  getShippingDocument: (id: string) =>
    api.get(`/shipping/documents/${id}`).then((r) => r.data),

  createShippingDocument: (body: {
    originLocationId: string;
    items?: { productId?: string; epc?: string; quantity: number }[];
  }) => api.post('/shipping/documents', body).then((r) => r.data),

  updateShippingDocument: (
    id: string,
    patch: {
      originLocationId?: string;
      items?: { productId?: string; epc?: string; quantity: number }[];
    },
  ) => api.patch(`/shipping/documents/${id}`, patch).then((r) => r.data),

  deleteShippingDocument: (id: string) =>
    api.delete(`/shipping/documents/${id}`).then((r) => r.data),

  getShippingDocumentByCode: (code: string) =>
    api.get(`/shipping/documents/by-code/${encodeURIComponent(code)}`).then((r) => r.data),

  scanShippingDocument: (id: string, payload: { epc: string; deviceSerial?: string }) =>
    api.post(`/shipping/documents/${id}/scan`, payload).then((r) => r.data),

  completeShippingDocument: (id: string, override = false) =>
    api.post(`/shipping/documents/${id}/complete`, { override }).then((r) => r.data),
};
